import {
  Component, ChangeDetectionStrategy, Input, Output, EventEmitter, ChangeDetectorRef, OnChanges, SimpleChanges
} from '@angular/core';
import cloneDeep from 'lodash-es/cloneDeep';
import isEqual from 'lodash-es/isEqual';

import { isEmpty } from '@app/util/util';
import { AutocompletePlaces } from '@search-bar/entities/autocomplete-places';
import { LoaderRingType } from '@design-system/components/atoms/loader-ring/entities/loader-ring.types';
import {
  AutoCompleteDestination, AutoCompleteDestinationHotel, AutoCompleteDestinationLocation
} from '@shared/base-search-bar/entities/autocomplete-destionation';
import { AutocompletePlace } from '@shared/base-search-bar/entities/autocomplete';
import { PlaceType } from '@shared/base-search-bar/entities/place-type';
import { SearchType } from '@search-bar/entities/search-type';
import { EventProperties } from '@avo/interface/trigger-props';
import { Icon, IconFillColor } from '@design-system/components/atoms/icon/entities/icon.types';
import { DropDownViewCloseAction } from '@ux/components/drop-down-view/entities/drop-down-view.types';
import {
  TypographyColor, TypographyFontFamily, TypographyFontSize
} from '@design-system/components/atoms/typography/entities/typography.types';
import { AutocompleteType } from '@shared/base-search-bar/entities/search-input-payload';
import {
  AutoCompleteExtraActivity, AutoCompleteExtraDestination, AutoCompleteTravelExtra
} from '@shared/base-search-bar/entities/autocomplete-travel-extra';
import { AutocompleteDefaultVisibility, AutocompleteVisibilityMax } from '@shared/base-search-bar/constants/autocomplete.const';

@Component({
  selector: 'app-search-input-autocomplete',
  templateUrl: './search-input-autocomplete.component.html',
  styleUrls: ['./search-input-autocomplete.component.sass'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class SearchInputAutocompleteComponent implements OnChanges {

  @Input() viewAutocomplete = false;
  @Input() isLoadingAutocompletePlaces = false;
  @Input() isMobile = true;
  @Input() cityAndAirportsAutocomplete = { airports: [], cities: [] } as AutocompletePlaces;
  @Input() hotelAndLocationsAutocomplete = { hotels: [], locations: [] } as AutoCompleteDestination;
  @Input() activityAndDestinationAutocomplete = { activities: [], destinations: [] } as AutoCompleteTravelExtra;
  @Input() searchType: SearchType;
  @Input() placeType: PlaceType;
  @Input() query: string;
  @Input() title: string;
  @Input() inputPlaceListLabel: string;
  @Input() inputPlaceListPlaceholder: string;
  @Input() inputPlaceListIcon: Icon;
  @Input() airportsAreEnabled = true;
  @Input() mergeAirportsAndCities = true;
  @Input() useIconInSubdirectories = false;
  @Input() isCollapsableList = false;

  @Output() closeAutocomplete: EventEmitter<void> = new EventEmitter<void>();
  @Output() setQuery: EventEmitter<string> = new EventEmitter<string>();
  @Output() popularAmplitudeEvent: EventEmitter<EventProperties> = new EventEmitter<EventProperties>();
  @Output() selectAirportOrCityEvent: EventEmitter<{
    place: AutocompletePlace, isAirport: boolean
  }> = new EventEmitter<{
    place: AutocompletePlace, isAirport: boolean
  }>();
  @Output() selectLocationEvent: EventEmitter<AutoCompleteDestinationLocation> =
    new EventEmitter<AutoCompleteDestinationLocation>();
  @Output() selectHotelEvent: EventEmitter<AutoCompleteDestinationHotel> = new EventEmitter<AutoCompleteDestinationHotel>();
  @Output() selectPopularPlace: EventEmitter<AutocompletePlace> = new EventEmitter<AutocompletePlace>();
  @Output() selectActivityEvent: EventEmitter<AutoCompleteExtraActivity> =
    new EventEmitter<AutoCompleteExtraActivity>();
  @Output() selectExtraDestinationEvent: EventEmitter<AutoCompleteExtraDestination> =
    new EventEmitter<AutoCompleteExtraDestination>();

  public visibility = cloneDeep(AutocompleteDefaultVisibility);

  public readonly autocompleteType = AutocompleteType;

  public readonly loaderRingType = LoaderRingType.Neutral;
  public readonly closeAction = DropDownViewCloseAction.Close;

  public readonly icons = Icon;
  public readonly iconFillColor = IconFillColor;

  public readonly fontColor = TypographyColor.Primary;
  public readonly fontSize = TypographyFontSize;
  public readonly fontFamily = TypographyFontFamily;

  constructor(
    private readonly cdr: ChangeDetectorRef,
  ) { }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      this.isCollapsableList && (
        changes?.isLoadingAutocompletePlaces && !isEqual(
          changes?.isLoadingAutocompletePlaces?.currentValue,
          changes?.isLoadingAutocompletePlaces?.previousValue
        )
      )
    ) {
      this.visibility = cloneDeep(AutocompleteDefaultVisibility);
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public getFilteredList(type: AutocompleteType): any[] {
    const list = this.getList(type);
    const visibility = this.visibility?.[type];
    return this.isCollapsableList ? list.slice(0, visibility?.Count) : list;
  }

  public cityHasMultipleAirports(airports: AutocompletePlace[]): boolean {
    return airports?.length > 1;
  }

  public cityAutocompleteLabel(place: AutocompletePlace): string {
    const description = !isEmpty(place.highlight_description) ? place.highlight_description : place.description;
    if (this.mergeAirportsAndCities) {
      const placeState = !isEmpty(place.state) ? `, ${place.state}` : '';
      return `${description}${placeState}, ${place.country}`;
    } else {
      return description;
    }
  }

  public cityAutocompleteDescription(place: AutocompletePlace): string {
    const placeState = !isEmpty(place.state) ? `${place.state}` : '';
    return placeState ? `${placeState}, ${place.country}` : place.country;
  }

  public viewAllAirports(airports: AutocompletePlace[]): boolean {
    return (airports?.length > 0 && !(airports?.length === 1)) || !(airports?.length > 0);
  }

  public fullNameByLocationAutocomplete(nameFull: string, name: string): string {
    if (name && nameFull?.toUpperCase().includes(`${name}, `.toUpperCase())) {
      return nameFull.slice(`${name}, `.length);
    } else {
      return nameFull;
    }
  }

  public selectAirportOrCity(place: AutocompletePlace, isAirport: boolean | undefined = false): void {
    this.selectAirportOrCityEvent.emit({ place, isAirport });
  }

  public toggleVisibility(listType: AutocompleteType): void {
    const list = this.getList(listType);
    const visibility = this.visibility?.[listType];
    visibility.ShowAll = !visibility.ShowAll;
    visibility.Count = visibility.ShowAll ? list.length : AutocompleteVisibilityMax;
  }

  public isShowToggleVisible(listType: AutocompleteType): boolean {
    return this.isCollapsableList && this.getList(listType)?.length > AutocompleteVisibilityMax;
  }

  public isLastItemVisible(listType: AutocompleteType, placeId: number): boolean {
    const visibility = this.visibility?.[listType];
    const listLength = this.getList(listType)?.length;
    return this.isCollapsableList && placeId === (visibility?.Count - 1) &&
      !visibility?.ShowAll && visibility?.Count < listLength;
  }

  public getShowAll(listType: AutocompleteType): boolean {
    return this.visibility?.[listType]?.ShowAll;
  }

  public closeView(): void {
    this.closeAutocomplete.emit();
    this.visibility = cloneDeep(AutocompleteDefaultVisibility);
    this.cdr.detectChanges();
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private getList(listType: AutocompleteType): any[] {
    if (listType === AutocompleteType.Airports || listType === AutocompleteType.Cities) {
      return this.cityAndAirportsAutocomplete?.[listType] ?? [];
    } else if (listType === AutocompleteType.Activities || listType === AutocompleteType.Destinations) {
      return this.activityAndDestinationAutocomplete?.[listType] ?? [];
    } else {
      return [];
    }
  }
}
